/**
 * Phase 11 — Customer invoice queries (read hooks).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface CustomerInvoiceRow {
  id: string;
  shop_id: string;
  ticket_id: string;
  invoice_number: string;
  status: 'estimate' | 'authorized' | 'finalized' | 'paid' | 'void';
  document_kind: 'estimate' | 'invoice';
  customer_id: string | null;
  customer_vehicle_id: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  vehicle_snapshot: Record<string, unknown>;
  shop_snapshot: Record<string, unknown>;
  parts_subtotal: number;
  labor_subtotal: number;
  subtotal: number;
  fees_total: number;
  tax_total: number;
  discount_total: number;
  grand_total: number;
  amount_paid: number;
  credit_balance: number;
  balance_due: number;
  recognized_revenue_at: string | null;
  converted_from_estimate_at: string | null;
  finalized_at: string | null;
  voided_at: string | null;
  void_reason: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomerInvoiceLineRow {
  id: string;
  invoice_id: string;
  shop_id: string;
  line_number: number;
  type: 'part' | 'labor' | 'fee' | 'discount' | 'core_charge' | 'tax';
  description: string;
  quantity: number;
  unit_price: number;
  unit_cost: number | null;
  extended_total: number;
  taxable: boolean;
  tax_amount: number;
  source_type: string | null;
  source_reference: Record<string, unknown> | null;
  notes: string | null;
}

export interface CustomerInvoiceAuthorizationRow {
  id: string;
  invoice_id: string;
  shop_id: string;
  type: 'pre_repair' | 'post_repair';
  customer_name: string;
  signature_png: string;
  authorization_text: string;
  authorized_amount: number;
  approved_scope: Array<Record<string, unknown>>;
  approved_subtotal: number;
  approved_tax: number;
  approved_grand_total: number;
  signed_at: string;
  ip_address: string | null;
  user_agent: string | null;
  captured_by_user: string | null;
}

export interface CustomerInvoicePaymentRow {
  id: string;
  invoice_id: string;
  shop_id: string;
  kind: 'payment' | 'refund';
  entered_amount: number;
  applied_amount: number;
  change_given: number;
  method: 'cash' | 'card' | 'check' | 'ach' | 'financing' | 'other';
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  transaction_reference: string | null;
  refund_of_payment_id: string | null;
  notes: string | null;
  recorded_by: string | null;
  recorded_at: string;
  voided_at: string | null;
  void_reason: string | null;
}

export interface CustomerInvoiceAuditRow {
  id: string;
  invoice_id: string;
  shop_id: string;
  event_type: string;
  actor_user: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface CustomerInvoicePdfRenderRow {
  id: string;
  invoice_id: string;
  shop_id: string;
  document_kind: 'estimate' | 'invoice';
  status_at_render: string;
  storage_path: string;
  content_hash: string;
  rendered_by: string | null;
  rendered_at: string;
}

/** All invoices for a single ticket (header rows only). */
export function useCustomerInvoicesForTicket(ticketId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['customer-invoices', 'ticket', ticketId],
    enabled: !!ticketId && enabled,
    queryFn: async () => {
      // FIX-R30-H-2: narrow select — InvoicingCard only renders
      // number/status/document_kind/grand_total/balance_due. Keep PII
      // (phone, email) and blob snapshots out of the React Query cache.
      const { data, error } = await (supabase as any)
        .from('customer_invoices')
        .select(`
          id,
          shop_id,
          ticket_id,
          invoice_number,
          status,
          document_kind,
          grand_total,
          amount_paid,
          balance_due,
          created_at,
          finalized_at,
          voided_at
        `)
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as CustomerInvoiceRow[];
    },
  });
}

/** Full invoice detail including lines, authorizations, payments, audit, PDFs. */
export function useCustomerInvoice(invoiceId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['customer-invoice', invoiceId],
    enabled: !!invoiceId && enabled,
    queryFn: async () => {
      // R125-M-9: narrow header select to explicit CustomerInvoiceRow
      // field list (line 8-40). Detail view legitimately renders full
      // invoice (including customer_phone/email/vehicle_snapshot), but
      // select('*') would auto-flow any future internal columns (e.g.
      // credit_card_last4, stripe_payment_intent_id) into the client cache.
      const [headerRes, linesRes, authsRes, paymentsRes, auditRes, pdfsRes] = await Promise.all([
        (supabase as any).from('customer_invoices').select('id, shop_id, ticket_id, invoice_number, status, document_kind, customer_id, customer_vehicle_id, customer_name, customer_phone, customer_email, vehicle_snapshot, shop_snapshot, parts_subtotal, labor_subtotal, subtotal, fees_total, tax_total, discount_total, grand_total, amount_paid, credit_balance, balance_due, recognized_revenue_at, converted_from_estimate_at, finalized_at, voided_at, void_reason, created_by, created_at, updated_at').eq('id', invoiceId).maybeSingle(),
        (supabase as any).from('customer_invoice_lines').select('*').eq('invoice_id', invoiceId).order('line_number'),
        (supabase as any).from('customer_invoice_authorizations').select('*').eq('invoice_id', invoiceId).order('signed_at'),
        (supabase as any).from('customer_invoice_payments').select('*').eq('invoice_id', invoiceId).order('recorded_at'),
        // FIX-R30-M-2: audit UI only renders created_at + event_type. The
        // `payload` JSONB field may carry before/after snapshots of
        // customer PII or financial mutations; don't pull it into the
        // client cache unless a caller opts in via a dedicated query.
        (supabase as any).from('customer_invoice_audit_log').select('id, invoice_id, shop_id, event_type, actor_user, created_at').eq('invoice_id', invoiceId).order('created_at', { ascending: false }),
        (supabase as any).from('customer_invoice_pdf_renders').select('*').eq('invoice_id', invoiceId).order('rendered_at', { ascending: false }),
      ]);
      if (headerRes.error) throw headerRes.error;
      if (linesRes.error) throw linesRes.error;
      if (authsRes.error) throw authsRes.error;
      if (paymentsRes.error) throw paymentsRes.error;
      if (auditRes.error) throw auditRes.error;
      if (pdfsRes.error) throw pdfsRes.error;
      return {
        header: headerRes.data as CustomerInvoiceRow | null,
        lines: (linesRes.data ?? []) as CustomerInvoiceLineRow[],
        authorizations: (authsRes.data ?? []) as CustomerInvoiceAuthorizationRow[],
        payments: (paymentsRes.data ?? []) as CustomerInvoicePaymentRow[],
        audit: (auditRes.data ?? []) as CustomerInvoiceAuditRow[],
        pdfs: (pdfsRes.data ?? []) as CustomerInvoicePdfRenderRow[],
      };
    },
  });
}

/** Shop-wide list with optional filters. */
export function useCustomerInvoicesForShop(filters?: {
  status?: string;
  search?: string;
}) {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;
  return useQuery({
    queryKey: ['customer-invoices', 'shop', shopId, filters?.status ?? 'all', filters?.search ?? ''],
    enabled: !!shopId,
    queryFn: async () => {
      // FIX-R30-H-1: narrow select — list view only renders
      // number/status/customer_name/total/balance/date. Withholding
      // customer_phone, customer_email, vehicle_snapshot, shop_snapshot,
      // and payment-details columns keeps PII and cached blobs out of
      // React Query's in-memory cache. Search still works because the
      // .or(...) clause filters server-side (doesn't require those
      // columns to be in the SELECT list).
      let q = (supabase as any)
        .from('customer_invoices')
        .select(`
          id,
          shop_id,
          ticket_id,
          invoice_number,
          status,
          document_kind,
          customer_id,
          customer_vehicle_id,
          customer_name,
          grand_total,
          amount_paid,
          balance_due,
          created_at,
          finalized_at,
          voided_at
        `)
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false })
        .limit(200);
      if (filters?.status && filters.status !== 'all') q = q.eq('status', filters.status);
      if (filters?.search) {
        const term = `%${filters.search}%`;
        q = q.or(
          `invoice_number.ilike.${term},customer_name.ilike.${term},customer_phone.ilike.${term}`,
        );
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as CustomerInvoiceRow[];
    },
  });
}

/** Shop invoicing settings (terms, pre-auth toggle). */
export function useShopInvoicingSettings() {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;
  return useQuery({
    queryKey: ['shop-invoicing-settings', shopId],
    enabled: !!shopId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('shop_invoicing_settings')
        .select('*')
        .eq('shop_id', shopId)
        .maybeSingle();
      if (error) throw error;
      return data as {
        id: string;
        shop_id: string;
        pre_repair_terms: string;
        post_repair_terms: string;
        require_pre_auth_before_work: boolean;
        invoice_number_prefix_year: boolean;
        created_at: string;
        updated_at: string;
      } | null;
    },
  });
}
